import { type RefObject, useCallback, useEffect, useRef } from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, type TrackingLocation, click } from '@tracking';

interface UseAnchorLinkTrackingProps {
  enabled?: boolean;
  textSelector?: string;
  trackingLocation?: TrackingLocation;
}

const ANCHOR_LINK_SELECTOR = 'a';

const useAnchorLinkTracking = <T extends HTMLElement = HTMLDivElement>({
  enabled = true,
  textSelector,
  trackingLocation,
}: UseAnchorLinkTrackingProps = {}): RefObject<T> => {
  const { trackEvent } = useEventTracking({
    location: trackingLocation,
  });
  const containerRef = useRef<T>(null);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const anchorLink = target.closest(ANCHOR_LINK_SELECTOR);
      if (!anchorLink) return;

      const text = textSelector
        ? (anchorLink.querySelector(textSelector) as HTMLElement | null)?.innerText
        : anchorLink.innerText;
      const link = anchorLink.href;

      trackEvent(
        click({
          clickTarget: ClickTarget.AnchorLink,
          clickText: text,
          clickLink: link,
        })
      );
    },
    [trackEvent, textSelector]
  );

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const container = containerRef.current;
    container.addEventListener('click', handleClick, { passive: true });
    return () => container.removeEventListener('click', handleClick);
  }, [enabled, handleClick]);

  return containerRef;
};

export default useAnchorLinkTracking;
